import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import SEOHead from '../components/shared/SEOHead';

const caseStudies = [
  {
    slug: 'str-enterprises',
    category: 'Culture review',
    client: 'STR Enterprises',
    headline: 'Protecting what works as a family business grows',
    sector: 'Hospitality',
    summary: 'Kalobrate listened to employees across the business, identified what should be protected as the company grew and helped leaders turn family values into practical decisions.',
  },
  {
    slug: 'dynatech-renewables',
    category: 'Culture by design',
    client: 'Dynatech Renewables',
    headline: 'Building the right culture before rapid growth',
    sector: 'Wind energy',
    summary: 'Kalobrate helped the founders define the values that would guide leadership, recruitment, onboarding and the way technicians were supported.',
  },
  {
    slug: 'bel-engineering',
    category: 'Workforce learning',
    client: 'BEL Engineering',
    headline: 'Making workplace learning relevant to an engineering workforce',
    sector: 'Manufacturing and engineering',
    summary: 'Kalobrate delivered practical learning across shifts and roles, making subjects such as sleep, stress and men\'s mental health relevant to an engineering workforce.',
  },
  {
    slug: 'cpt',
    category: 'Speaking and facilitation',
    client: 'CPT',
    headline: 'Helping transport leaders make sense of mental health at work',
    sector: 'Bus and coach transport',
    summary: 'Kalobrate helped bus and coach leaders understand workplace mental health through practical talks designed around the pressures of the transport sector.',
  },
];

const ResultsPage = () => (
  <PageLayout>
    <SEOHead
      title="Client Results and Case Studies | Kalobrate"
      description="See how Kalobrate has supported organisations through workplace listening, culture reviews, leadership support, workforce learning and practical sector engagement."
      canonical="/results"
    />

    <section className="bg-carbon py-20 md:py-28 relative overflow-hidden">
      <img src="/assets/kalobrate-icon-outline-yellow.png" alt="" aria-hidden="true" className="absolute top-[-10%] right-[-5%] w-[55%] h-[120%] object-contain opacity-[0.06] pointer-events-none select-none" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1
          className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight tracking-tight mb-6"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        >
          Work grounded in <span className="yellow-highlight text-carbon">real</span> organisations
        </motion.h1>
        <motion.p
          className="text-base text-white/80 leading-relaxed max-w-2xl"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
        >
          Kalobrate works with organisations at different stages. Some want to understand an existing problem. Others want to protect their culture during growth, support managers or make difficult workplace subjects easier to address.
        </motion.p>
        <motion.p
          className="text-base text-white/70 leading-relaxed mt-4"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
        >
          These case studies show the different ways that work can begin and what clients receive.
        </motion.p>
      </div>
    </section>

    <section className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-6">
          {caseStudies.map((cs, i) => (
            <motion.div
              key={cs.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
            >
              <Link
                to={`/results/${cs.slug}`}
                className="block bg-white border border-gray-100 p-8 group hover-lift h-full"
                data-testid={`case-study-card-${cs.slug}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-block bg-golden/15 text-carbon text-xs font-semibold px-3 py-1">{cs.category}</span>
                  <span className="text-xs text-carbon/40">{cs.sector}</span>
                </div>
                <h2 className="text-xl font-heading font-bold text-carbon mb-2 group-hover:text-golden transition-colors">{cs.headline}</h2>
                <p className="text-xs text-carbon/50 mb-3">{cs.client}</p>
                <p className="text-sm text-carbon/70 leading-relaxed mb-6">{cs.summary}</p>
                <span className="inline-flex items-center text-sm font-semibold text-carbon group-hover:text-golden transition-colors">
                  View case study <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-golden py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-carbon mb-4 tracking-tight">Facing a similar issue?</h2>
        <p className="text-base text-carbon/80 mb-8 max-w-xl mx-auto leading-relaxed">
          The first step is understanding what is happening in your organisation and what evidence is already available.
        </p>
        <Link to="/contact" className="bg-carbon text-white px-7 py-3.5 font-semibold text-sm hover:bg-carbon/90 transition-colors inline-flex items-center">
          Talk to Ollie <ArrowRight className="ml-2 w-4 h-4" />
        </Link>
      </div>
    </section>
  </PageLayout>
);

export default ResultsPage;
